import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Music, ThumbsUp, ThumbsDown, Plus } from "lucide-react";
import { useState } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";

interface PlaylistTrack {
  id: string;
  track_name: string;
  artist_name: string;
  track_bpm?: number;
  votes_count: number;
}

interface CollaborativePlaylistEditorProps {
  playlistId: string;
  playlistName: string;
  tracks: PlaylistTrack[];
  onAddTrack: (playlistId: string, trackName: string, artistName: string, bpm?: number) => void;
  onVote: (trackId: string, voteType: 'up' | 'down') => void;
}

export const CollaborativePlaylistEditor = ({
  playlistId,
  playlistName,
  tracks,
  onAddTrack,
  onVote,
}: CollaborativePlaylistEditorProps) => {
  const [newTrackName, setNewTrackName] = useState('');
  const [newArtistName, setNewArtistName] = useState('');
  const [newTrackBpm, setNewTrackBpm] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  const handleAdd = () => {
    if (!newTrackName || !newArtistName) return;
    
    onAddTrack(
      playlistId,
      newTrackName,
      newArtistName,
      newTrackBpm ? parseInt(newTrackBpm) : undefined
    );
    
    setNewTrackName('');
    setNewArtistName('');
    setNewTrackBpm('');
    setIsAdding(false);
  };

  return (
    <Card className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Music className="w-5 h-5 text-primary" />
          <h3 className="font-semibold">{playlistName}</h3>
          <Badge variant="outline">{tracks.length} músicas</Badge>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setIsAdding(!isAdding)}
          className="gap-1"
        >
          <Plus className="w-4 h-4" />
          Adicionar
        </Button>
      </div>

      {isAdding && (
        <Card className="p-3 space-y-2 bg-accent/50">
          <Input
            placeholder="Nome da música"
            value={newTrackName}
            onChange={(e) => setNewTrackName(e.target.value)}
          />
          <Input
            placeholder="Nome do artista"
            value={newArtistName}
            onChange={(e) => setNewArtistName(e.target.value)}
          />
          <Input
            placeholder="BPM (opcional)"
            type="number"
            value={newTrackBpm}
            onChange={(e) => setNewTrackBpm(e.target.value)}
          />
          <div className="flex gap-2">
            <Button onClick={handleAdd} size="sm" className="flex-1">
              Adicionar Música
            </Button>
            <Button
              onClick={() => setIsAdding(false)}
              size="sm"
              variant="outline"
            >
              Cancelar
            </Button>
          </div>
        </Card>
      )}

      <ScrollArea className="h-[400px] pr-4">
        <div className="space-y-2">
          {tracks.map((track, index) => (
            <Card key={track.id} className="p-3 hover:bg-accent/50 transition-colors">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className="text-sm font-semibold text-muted-foreground">
                    #{index + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{track.track_name}</p>
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-muted-foreground truncate">
                        {track.artist_name}
                      </p>
                      {track.track_bpm && (
                        <Badge variant="secondary" className="text-xs">
                          {track.track_bpm} BPM
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onVote(track.id, 'up')}
                    className="h-8 w-8 p-0"
                  >
                    <ThumbsUp className="w-4 h-4" />
                  </Button>
                  <span className="text-sm font-semibold min-w-[2rem] text-center">
                    {track.votes_count}
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onVote(track.id, 'down')}
                    className="h-8 w-8 p-0"
                  >
                    <ThumbsDown className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
          {tracks.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <Music className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Nenhuma música ainda. Seja o primeiro a adicionar!</p>
            </div>
          )}
        </div>
      </ScrollArea>
    </Card>
  );
};
